import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { TriangleInvertedFilled } from '../Icons/Triangle';

interface DropdownButtonProps {
  /** List of options to show */
  options: string[];
  /** Controlled selected value */
  value?: string;
  /** Initial selection for uncontrolled mode */
  initialSelection?: string;
  /** Called whenever the selection changes */
  onSelect?: (option: string) => void;
  /** Optional className for further styling */
  className?: string;
}

const DropdownButton: React.FC<DropdownButtonProps> = ({
  options,
  value,
  initialSelection,
  onSelect,
  className,
}) => {
  const isControlled = value !== undefined;
  const [open, setOpen] = useState(false);
  const [internalSelected, setInternalSelected] = useState(
    initialSelection ?? options[0] ?? ''
  );
  const selected = isControlled ? value! : internalSelected;
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Sync internal state to controlled value when it changes
  useEffect(() => {
    if (isControlled) {
      setInternalSelected(value!);
    }
  }, [value, isControlled]);

  const toggleOpen = () => setOpen((o) => !o);
  const selectOption = (opt: string) => {
    if (!isControlled) {
      setInternalSelected(opt);
    }
    onSelect?.(opt);
    setOpen(false);
  };

  // Close when clicking outside
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <DropdownWrapper ref={wrapperRef} className={className}>
      <ToggleButton onClick={toggleOpen}>
        {selected}
        <TriangleInvertedFilled />
      </ToggleButton>

      {open && (
        <OptionsList>
          {options.map((opt) => (
            <OptionItem key={opt} onClick={() => selectOption(opt)}>
              {opt}
            </OptionItem>
          ))}
        </OptionsList>
      )}
    </DropdownWrapper>
  );
};

export default DropdownButton;

/** Container to position the dropdown */
const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;

  @media (max-width: 768px) {
    width: 100%;
    font-size: 16px;
    padding: 0 16px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

/** Matches your button style */
const ToggleButton = styled.button`
  width: 238px;
  height: 54px;
  border-radius: 4px;
  font-size: 20px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  background-color: ${({ theme }) => theme.palette.mainBlue};
  color: white;
  border: none;

  & svg {
    min-width: 14px;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    font-size: 16px;
    padding: 0 16px;
  }
  @media (max-width: 480px) {
    white-space: nowrap;
    height: 45px;
    width: 9rem;
    font-size: 14px;
  }
`;

/** The dropdown’s menu */
const OptionsList = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 238px;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid #828282;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

/** Each option */
const OptionItem = styled.li`
  padding: 0.75rem 1rem;
  font-size: 20px;
  color: #828282;
  cursor: pointer;

  &:hover {
    background: #f0f0f0;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 0.5rem 16px;
  }
  @media (max-width: 390px) {
    font-size: 14px;
  }
`;
